import './WebHitsPanel.css'
import { useApi } from '@/hooks/useApi'
import { useInterval } from '@/hooks/useInterval'
import { formatDecimal } from '@/utilities/formatters'
import { websiteHitQuerySpec, type WebsiteHitRow } from 'aevocam-rest-api-spec'
import { useCallback, useEffect, useState } from 'react'
import { Spinner } from '@/components/graphics/Spinner'
import { Sparkline } from '@/components/graphics/Sparkline'
import { formatSparklineTooltip } from '../telemetry/telemetryHelpers'
import {
  formatDurationMs,
  rowsToWebHitCards,
  WEB_HIT_DAILY_BUCKET_COUNT,
  WEB_HIT_DAILY_PERIOD_SEC,
  WEB_HIT_HOURLY_BUCKET_COUNT,
  WEB_HIT_HOURLY_PERIOD_SEC,
  WEB_HIT_REFRESH_MS,
  WEB_HIT_TIME_ZONE,
  webHitDailyWindow,
  webHitHourlyWindow,
  type WebHitCard,
} from './webHitHelpers'

export function WebHitsPanel() {
  const api = useApi({
    requireLogin: true,
    adminOnly: true,
  })

  const [cards, setCards] = useState<WebHitCard[]>([])
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    const { from: hourlyFrom, to: hourlyTo, now } = webHitHourlyWindow()
    const { from: dailyFrom, to: dailyTo } = webHitDailyWindow()

    const res = await api.request(websiteHitQuerySpec, {
      body: {
        timezone: WEB_HIT_TIME_ZONE,
        queries: {
          hourly: {
            aggregatePer: 'hour',
            createdAtFrom: hourlyFrom.toISOString(),
            createdAtTo: hourlyTo.toISOString(),
          },
          daily: {
            aggregatePer: 'day',
            createdAtFrom: dailyFrom.toISOString(),
            createdAtTo: dailyTo.toISOString(),
          },
          hourlyDuration: {
            aggregatePer: 'all',
            withDurationStats: true,
            createdAtFrom: hourlyFrom.toISOString(),
            createdAtTo: hourlyTo.toISOString(),
          },
          dailyDuration: {
            aggregatePer: 'all',
            withDurationStats: true,
            createdAtFrom: dailyFrom.toISOString(),
            createdAtTo: dailyTo.toISOString(),
          },
        },
      },
    })

    const hourlyRows = (res.data?.results?.hourly ?? []) as WebsiteHitRow[]
    const dailyRows = (res.data?.results?.daily ?? []) as WebsiteHitRow[]
    const hourlyDurationRows = (res.data?.results?.hourlyDuration ?? []) as WebsiteHitRow[]
    const dailyDurationRows = (res.data?.results?.dailyDuration ?? []) as WebsiteHitRow[]

    setCards(rowsToWebHitCards(
      hourlyRows,
      dailyRows,
      hourlyDurationRows,
      dailyDurationRows,
      hourlyFrom,
      hourlyTo,
      dailyFrom,
      dailyTo,
      now,
    ))
    setLoading(false)
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useInterval(refresh, WEB_HIT_REFRESH_MS)

  return (
    <div className='WebHitsPanel Cutout Wrapped'>
      <h2>Web hits</h2>

      {loading && cards.length === 0 && (
        <Spinner />
      )}

      {!loading && cards.length === 0 && (
        <p className='Empty'>No web hit data yet.</p>
      )}

      {cards.length > 0 && (
        <div className='MetricGrid'>
          {cards.map(item => (
            <div key={item.key} className='MetricCard'>
              <div className='Section'>
                <p className='Caption'>{WEB_HIT_HOURLY_BUCKET_COUNT} hours</p>
                <div className='ValueRow'>
                  <span className='Value'>{formatDecimal(Math.round(item.hourlyAverage))}</span>
                  <span className='Unit'>/ hour</span>
                </div>
                {(item.hourlyDurationAvgMs != null || item.hourlyDurationMaxMs != null) && (
                  <p className='Duration'>
                    {item.hourlyDurationAvgMs != null && (
                      <>Avg: {formatDurationMs(item.hourlyDurationAvgMs)}</>
                    )}
                    {item.hourlyDurationAvgMs != null && item.hourlyDurationMaxMs != null && ' | '}
                    {item.hourlyDurationMaxMs != null && (
                      <>Max: {formatDurationMs(item.hourlyDurationMaxMs)}</>
                    )}
                  </p>
                )}
                <Sparkline
                  values={item.buckets}
                  formatTooltip={index => formatSparklineTooltip(
                    index,
                    item.buckets,
                    item.bucketFrom,
                    WEB_HIT_HOURLY_PERIOD_SEC,
                  )}
                />
              </div>
              <div className='Section DailySection'>
                <p className='Caption'>{WEB_HIT_DAILY_BUCKET_COUNT} days</p>
                <div className='ValueRow'>
                  <span className='Value Daily'>{formatDecimal(Math.round(item.dailyAverage))}</span>
                  <span className='Unit'>/ day</span>
                </div>
                {(item.dailyDurationAvgMs != null || item.dailyDurationMaxMs != null) && (
                  <p className='Duration'>
                    {item.dailyDurationAvgMs != null && (
                      <>Avg: {formatDurationMs(item.dailyDurationAvgMs)}</>
                    )}
                    {item.dailyDurationAvgMs != null && item.dailyDurationMaxMs != null && ' | '}
                    {item.dailyDurationMaxMs != null && (
                      <>Max: {formatDurationMs(item.dailyDurationMaxMs)}</>
                    )}
                  </p>
                )}
                <Sparkline
                  values={item.dailyBuckets}
                  formatTooltip={index => formatSparklineTooltip(
                    index,
                    item.dailyBuckets,
                    item.dailyBucketFrom,
                    WEB_HIT_DAILY_PERIOD_SEC,
                  )}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
